/**
 * Optional client-side audit beacon to trade-data-server.
 * Server prefers proxy headers; when Node only sees 127.0.0.1, we send client_reported_ip.
 * Requires JWT + userId in localStorage (same as other Node APIs).
 */
import APIMiddleware from '../data/api/Api-Middleware';
import { API_ENDPOINT_AUDIT_MANUAL_ACTION } from '../data/Endpoints-API';
import { readStoredVariantId } from './sessionVariantId';

export const AUDIT_CLIENT_IP_KEY = 'audit_client_public_ip';

function isValidIpv4(ip) {
  const m = String(ip || '').match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return false;
  return m.slice(1, 5).every((oct) => {
    const n = Number(oct);
    return Number.isInteger(n) && n >= 0 && n <= 255;
  });
}

let clientPublicIpPromise = null;

export function getCachedClientPublicIp() {
  if (typeof window === 'undefined') return null;
  const cached = sessionStorage.getItem(AUDIT_CLIENT_IP_KEY);
  return cached && isValidIpv4(cached) ? cached : null;
}

/** Append client_reported_ip for GET routes (e.g. logout) when proxy only sees 127.0.0.1 */
export async function appendClientReportedIpToUrl(url) {
  const ip = (await resolveClientPublicIp()) || getCachedClientPublicIp();
  if (!ip) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}client_reported_ip=${encodeURIComponent(ip)}`;
}

/** Best-effort public IP for audit when reverse proxy does not forward X-Forwarded-For. */
export function resolveClientPublicIp() {
  if (typeof window === 'undefined') return Promise.resolve(null);

  const cached = sessionStorage.getItem(AUDIT_CLIENT_IP_KEY);
  if (cached && isValidIpv4(cached)) return Promise.resolve(cached);

  if (!clientPublicIpPromise) {
    clientPublicIpPromise = fetch('https://api.ipify.org?format=json', {
      method: 'GET',
      signal: typeof AbortSignal !== 'undefined' ? AbortSignal.timeout(4000) : undefined,
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const ip = data?.ip;
        if (isValidIpv4(ip)) {
          sessionStorage.setItem(AUDIT_CLIENT_IP_KEY, ip);
          return ip;
        }
        return null;
      })
      .catch(() => null)
      .finally(() => {
        clientPublicIpPromise = null;
      });
  }

  return clientPublicIpPromise;
}

export async function buildClientAuditMeta() {
  if (typeof window === 'undefined') return {};
  const ua = navigator.userAgent || '';
  let client_reported_surface = 'web';
  if (/Mobi|Android.*Mobile|iPhone|iPod|Windows Phone|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    client_reported_surface = 'mobile_web';
  } else if (/iPad|Tablet|Kindle|Silk|PlayBook/i.test(ua)) {
    client_reported_surface = 'tablet_web';
  } else if (/Android/i.test(ua) && !/Mobile/i.test(ua)) {
    client_reported_surface = 'tablet_web';
  }

  const client_reported_ip = await resolveClientPublicIp();

  return {
    timezone: Intl.DateTimeFormat?.().resolvedOptions?.().timeZone,
    language: navigator.language,
    screen:
      typeof window !== 'undefined' && window.screen
        ? `${window.screen.width}x${window.screen.height}`
        : null,
    platform: navigator.platform,
    href: window.location?.href?.slice(0, 500),
    client_reported_surface,
    ...(client_reported_ip ? { client_reported_ip } : {}),
    max_touch_points:
      typeof navigator.maxTouchPoints === 'number'
        ? navigator.maxTouchPoints
        : undefined,
    user_agent_snippet: ua.slice(0, 400),
  };
}

/**
 * @param {string} action - must be allowed on server (see audit-c.js ALLOWED_CLIENT_ACTIONS)
 * @param {Record<string, unknown>} [payload]
 */
export function getClientVariantId() {
  return readStoredVariantId();
}

export async function logClientManualAction(action, payload = {}) {
  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('token');
  if (!userId || !token) return;

  const variant_id = getClientVariantId();

  try {
    const clientMeta = await buildClientAuditMeta();
    await APIMiddleware.post(API_ENDPOINT_AUDIT_MANUAL_ACTION(), {
      userId: Number(userId),
      action,
      ...(variant_id ? { variant_id } : {}),
      payload,
      clientMeta,
    });
  } catch (e) {
    console.warn('[manualActionAudit]', action, e?.response?.status || e.message);
  }
}
